"use client";

import { externalLinkProps } from "@/lib/external-link-props";
import Link from "next/link";
import { Calendar, ChevronDown } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { analytics } from "@/lib/analytics";
import { cn } from "@/lib/utils";

import { sortRomaReserveLocations, type RomaReserveLocation } from "./roma-types";

type RomaReserveMenuProps = {
  locations: readonly RomaReserveLocation[];
  allLocationsHref?: string;
  className?: string;
  buttonClassName?: string;
  onOpenMobileSheet?: () => void;
  onLocationSelect?: (slug: RomaReserveLocation["slug"]) => void;
};

const MOBILE_RESERVE_BREAKPOINT = "(max-width: 860px)";

export function RomaReserveMenu({
  locations,
  allLocationsHref = "/reservations#picker",
  className,
  buttonClassName,
  onOpenMobileSheet,
  onLocationSelect,
}: RomaReserveMenuProps) {
  const sortedLocations = sortRomaReserveLocations(locations);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const closeMenu = useCallback(() => {
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, open]);

  if (sortedLocations.length === 0) {
    return null;
  }

  const handleTriggerClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_RESERVE_BREAKPOINT).matches
    ) {
      closeMenu();
      onOpenMobileSheet?.();
      return;
    }

    setOpen((current) => !current);
  };

  return (
    <div ref={menuRef} className={cn("relative", className)}>
      <Button
        type="button"
        size="sm"
        aria-expanded={open}
        aria-haspopup="menu"
        className={cn(
          "min-h-10 rounded-xl px-4 text-sm font-medium tracking-[0.01em]",
          buttonClassName,
        )}
        onClick={handleTriggerClick}
      >
        <Calendar className="mr-1.5 h-4 w-4" />
        Reserve
        <ChevronDown
          className={cn(
            "ml-1 h-4 w-4 transition-transform duration-200",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </Button>

      <div
        role="menu"
        hidden={!open}
        className={cn(
          "absolute right-0 top-[calc(100%+0.5rem)] z-50 min-w-[15rem] rounded-xl border border-[var(--border,#e3e3e3)] bg-[var(--popover,#ffffff)] p-1.5 shadow-[var(--shadow-soft,0_18px_44px_-22px_rgb(0_0_0_/_0.28))]",
          !open && "pointer-events-none",
        )}
      >
        {sortedLocations.map((location) => (
          <a
            key={location.slug}
            role="menuitem"
            href={location.reserveUrl}
            {...externalLinkProps(location.reserveUrl)}
            className="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-[var(--popover-foreground,#2b1c17)] transition-colors hover:bg-[var(--accent,#f6f4ef)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta,#f45c24)]"
            /* The header nav sits outside [data-homepage], so the delegated
               data-track listener never sees these clicks. Fire the
               reservation conversion explicitly instead. */
            onClick={() => {
              analytics.reservationClicked("roma_reserve_menu", {
                label: location.slug,
                cta_text: "Reserve",
                destination_url: location.reserveUrl,
              });
              onLocationSelect?.(location.slug);
              closeMenu();
            }}
          >
            {location.eyebrow}
          </a>
        ))}
        <Link
          role="menuitem"
          href={allLocationsHref}
          className="flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-[var(--popover-foreground,#2b1c17)] transition-colors hover:bg-[var(--accent,#f6f4ef)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--roma-terracotta,#f45c24)]"
          onClick={() => {
            analytics.reservationClicked("roma_reserve_menu", {
              label: "all-locations",
              cta_text: "All locations",
              destination_url: allLocationsHref,
            });
            closeMenu();
          }}
        >
          All locations
        </Link>
      </div>
    </div>
  );
}
